import { Filter } from 'lucide-react';
import type { SavedDashboardFilter } from './types';

interface DashboardFilterBarProps {
  savedFilters: SavedDashboardFilter[];
  activeFilterPresetId: number | null;
  onLoadPreset: (preset: SavedDashboardFilter, skipFetch?: boolean) => void;
  showFilters: boolean;
  setShowFilters: (show: boolean) => void;
  activeFilterCount: number;
  onRefresh: () => void;
  onExportReport?: () => void;
  exporting?: boolean;
}

/**
 * CR-6401: the filter header bar.
 *
 * Split out of DashboardHeader so the page follows the agreed layout:
 * page header -> tab bar -> filter header -> filter sections -> filter footer.
 * The bar stays visible when the filter sections are collapsed, so the active
 * filter count and the Refresh / Export / Show-filters actions remain reachable.
 */
export function DashboardFilterBar({
  savedFilters,
  activeFilterPresetId,
  onLoadPreset,
  showFilters,
  setShowFilters,
  activeFilterCount,
  onRefresh,
  onExportReport,
  exporting,
}: DashboardFilterBarProps) {
  return (
    <div className="px-6 py-3 border-t flex items-center justify-between gap-4 flex-wrap">
      {/* The label only appears when filters are actually applied — as a
          bare decoration on every tab it just claimed space. */}
      <div className="flex items-center gap-3">
        {activeFilterCount > 0 && (
          <>
            <span className="inline-flex items-center gap-2 text-sm font-medium text-foreground">
              <Filter className="h-4 w-4 text-muted-foreground" />
              Filters
            </span>
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium text-primary bg-primary/10">
              {activeFilterCount} {activeFilterCount === 1 ? 'filter' : 'filters'} active
            </span>
          </>
        )}
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        {/* Existing functionality retained — not in the diagram, but the ticket
            requires current behaviour be preserved. */}
        {savedFilters.length > 0 && (
          <select
            value={activeFilterPresetId?.toString() || ''}
            onChange={(e) => {
              const preset = savedFilters.find((f) => f.id.toString() === e.target.value);
              if (preset) onLoadPreset(preset);
            }}
            className="px-4 py-2 border border-border rounded-lg hover:bg-muted bg-background"
          >
            <option value="" disabled>Load saved filter...</option>
            {savedFilters.map((filter) => (
              <option key={filter.id} value={filter.id}>
                {filter.is_default && '⭐ '}{filter.name}
              </option>
            ))}
          </select>
        )}
        <button
          onClick={onRefresh}
          className="px-4 py-2 border border-border rounded-lg hover:bg-muted"
        >
          Refresh
        </button>
        <button
          onClick={onExportReport}
          disabled={exporting}
          className="px-4 py-2 border border-border rounded-lg hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {exporting ? 'Exporting...' : 'Export report'}
        </button>
        <button
          onClick={() => setShowFilters(!showFilters)}
          className={`px-4 py-2 border rounded-lg ${
            showFilters
              ? 'bg-primary text-primary-foreground border-primary'
              : 'border-border hover:bg-muted'
          }`}
        >
          {showFilters ? 'Hide filters' : 'Show filters'}
        </button>
      </div>
    </div>
  );
}
